/**
 * Design tokens — единый источник правды для палитры, типографики, spacing,
 * shadows и радиусов. Все компоненты импортируют отсюда, не хардкодят значения.
 *
 * Соответствует docs/DESIGN_SYSTEM.md.
 */

export const palette = {
  background: {
    default: '#FBF8F2',   // ivory page background
    paper: '#FFFFFF',
    elevated: 'rgba(168, 133, 75, 0.04)',
  },
  primary: {
    main: '#A8854B',      // bronze gold
    dark: '#8B6E3D',
    light: '#C9A961',
    contrastText: '#FFFFFF',
  },
  text: {
    primary: '#1A1815',
    secondary: '#6B6964',
    disabled: '#9F9C95',
  },
  divider: 'rgba(168, 133, 75, 0.18)',
  success: { main: '#6B8E5A', light: '#8FAE7F', dark: '#557048' },
  warning: { main: '#C9A961', light: '#D6BC85', dark: '#A4884E' },
  error:   { main: '#B5453C', light: '#D27267', dark: '#8E342D' },
  info:    { main: '#7B9EAF', light: '#A2BCC9', dark: '#5E7E8E' },
  // Custom — для leads и nuanced состояний
  violet:  { main: '#9B7BAE', light: '#B69BC4', dark: '#7C5E8D' },
} as const;

export const segmentColors = {
  angel:              '#C9A961',
  networker:          '#7B9EAF',
  quiet_connector:    '#6B8E5A',
  performer_mature:   '#C9A961',
  performer_fragile:  '#D89A6B',
  expert_enclave:     '#9B7BAE',
  random_guest:       '#9F9C95',
  elder_curator:      '#A8854B',
  fragile_newbie:     '#7B9EAF',
  undefined:          '#A5A39E',
} as const;

export const zoneColors = {
  red:    palette.error.main,
  yellow: palette.warning.main,
  green:  palette.success.main,
} as const;

export const stateColors = {
  active:  palette.success.main,
  churned: '#6B6964',
  frozen:  palette.info.main,
  lead:    palette.violet.main,
  other:   palette.text.disabled,
} as const;

// Typography
export const fontFamily = {
  // serif в дизайн-системе оставлен как алиас на sans (без засечек по решению 2026-05-19).
  serif: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  sans:  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  mono:  '"JetBrains Mono", "Menlo", Consolas, monospace',
} as const;

export const typography = {
  h1: {
    fontFamily: fontFamily.serif,
    fontWeight: 500,
    fontSize: '36px',
    lineHeight: 1.15,
    letterSpacing: '-0.01em',
  },
  h2: {
    fontFamily: fontFamily.serif,
    fontWeight: 500,
    fontSize: '24px',
    lineHeight: 1.25,
    letterSpacing: '-0.005em',
  },
  h3: {
    fontFamily: fontFamily.sans,
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: 1.3,
  },
  h4: {
    fontFamily: fontFamily.sans,
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: 1.4,
  },
  subtitle1: {
    fontFamily: fontFamily.sans,
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: 1.45,
  },
  subtitle2: {
    fontFamily: fontFamily.sans,
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: 1.4,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
  },
  body1: {
    fontFamily: fontFamily.sans,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: 1.55,
  },
  body2: {
    fontFamily: fontFamily.sans,
    fontWeight: 400,
    fontSize: '13px',
    lineHeight: 1.5,
  },
  caption: {
    fontFamily: fontFamily.sans,
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: 1.45,
  },
  overline: {
    fontFamily: fontFamily.sans,
    fontWeight: 500,
    fontSize: '10px',
    lineHeight: 1,
    letterSpacing: '0.14em',
    textTransform: 'uppercase' as const,
  },
  button: {
    fontFamily: fontFamily.sans,
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: 1,
    letterSpacing: '-0.005em',
    textTransform: 'none' as const,
  },
  mono: {
    fontFamily: fontFamily.mono,
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: 1.45,
  },
} as const;

// Spacing (MUI base = 4)
export const spacingBase = 4;

// Shape (radii)
export const radii = {
  xs: 4,
  sm: 6,
  md: 10,
  lg: 16,
  xl: 24,
  pill: 9999,
} as const;

// Shadows
export const shadows = {
  0: 'none',
  1: '0 1px 2px rgba(26, 24, 21, 0.04)',
  2: '0 2px 8px rgba(26, 24, 21, 0.06)',
  3: '0 4px 12px rgba(26, 24, 21, 0.08)',
  4: '0 8px 20px rgba(26, 24, 21, 0.10)',
  8: '0 24px 48px -12px rgba(26, 24, 21, 0.18)',
  elevatedBronze: '0 12px 32px -8px rgba(168, 133, 75, 0.30), 0 4px 10px rgba(26, 24, 21, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.18)',
  panel: '0 32px 64px -16px rgba(26, 24, 21, 0.25), 0 0 0 1px rgba(168, 133, 75, 0.04)',
} as const;

// Animation
export const transitions = {
  duration: {
    fast: 140,
    base: 200,
    slow: 320,
  },
  easing: {
    standard: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
    decelerate: 'cubic-bezier(0.16, 1, 0.3, 1)',
    accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
  },
} as const;

// Z-index scale
export const zIndex = {
  appBar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  floatingPanel: 2147483646,
  floatingLauncher: 2147483647,
} as const;

// Layout
export const layout = {
  sidebarWidth: 240,
  topbarHeight: 56,
  pageMaxWidth: 1440,
  aiPanelWidth: 480,
} as const;
